import CreateIcon from '@mui/icons-material/Create'
import {
  Box,
  ClickAwayListener,
  Stack,
  useTheme,
  IconButton,
  TextField,
  Typography,
  lighten,
  SxProps,
} from '@mui/material'
import React, { useRef, useEffect, useState } from 'react'

import rem from '~/utils/ui'

type TProps = {
  name: string
  ariaLabel: string
  value: string
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  error?: boolean
  helperText?: string
  onSubmit?: () => void
  placeholder?: string
  sxInputProps?: SxProps
  sxTitleProps?: SxProps
  sxIconProps?: SxProps
  disabled?: boolean
}

function EditableTitle({
  name,
  ariaLabel,
  value,
  onChange,
  error = false,
  helperText = '',
  onSubmit = undefined,
  placeholder = '',
  sxInputProps = {},
  sxTitleProps = {},
  sxIconProps = {},
  disabled = false,
}: TProps) {
  const [isEditing, setIsEditing] = useState(false)
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleEditClick = () => {
    setIsEditing(true)
    inputRef.current?.focus()
  }

  const validateAndSave = () => {
    if (value.trim() === '' && !placeholder) {
      inputRef.current?.focus()
    } else {
      setIsEditing(false)
      if (onSubmit) {
        onSubmit()
      }
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsEditing(false)
    } else if (event.key === 'Enter') {
      validateAndSave()
    }
  }

  const handleClickAway = () => {
    if (isEditing) {
      validateAndSave()
    }
  }

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
    }
  }, [isEditing])

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box minWidth={0}>
        {isEditing ? (
          <TextField
            name={name}
            inputRef={inputRef}
            value={value}
            onChange={onChange}
            onBlur={handleClickAway}
            onKeyDown={handleKeyDown}
            error={error}
            helperText={helperText}
            variant="standard"
            fullWidth
            sx={{
              '& .MuiInputBase-input': {
                fontSize: rem(34),
                color: theme.palette.mainFont,
                padding: 0,
                width: rem(480),
                height: rem(51),
              },
              ...sxInputProps,
            }}
            inputProps={{
              'aria-label': ariaLabel,
            }}
            disabled={disabled}
          />
        ) : (
          <Stack>
            <Stack direction="row" spacing={1}>
              <Typography
                variant="h4"
                sx={{
                  lineHeight: 'unset',
                  color: value
                    ? theme.palette.mainFont
                    : lighten(theme.palette.mainFont, 0.5),
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word',
                  ...sxTitleProps,
                }}
                onClick={() => (!value ? handleEditClick() : undefined)}
              >
                {value || placeholder}
              </Typography>
              {!disabled && (
                <IconButton
                  onClick={handleEditClick}
                  size="small"
                  aria-label="edit"
                  sx={sxIconProps}
                >
                  <CreateIcon
                    fontSize="large"
                    htmlColor={theme.palette.tertiary1}
                  />
                </IconButton>
              )}
            </Stack>
            {error && (
              <Typography variant="caption" color="error">
                {helperText}
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </ClickAwayListener>
  )
}

export default EditableTitle
